import ApiClient from '../ApiClient';
import { NiceResponse } from '../dto/NiceResponse';
import {
  InitUploadRequest,
  FinalizeUploadRequest,
  AddVideoRequest,
  AttachMediaRequest,
  DetachMediaRequest,
  MediaListParams,
} from './dto/MediaRequest.dto';
import {
  InitUploadResponse,
  FinalizeUploadResponse,
  AddVideoResponse,
  MediaAssetResponse,
  MediaListResponse,
  MediaActionResponse,
} from './dto/MediaResponse.dto';

export class MediaApi {
  // eslint-disable-next-line no-unused-vars
  constructor(private api: ApiClient) {}

  path = '/admin/media';

  /**
   * Инициализация загрузки изображения
   * Получает presigned URL для прямой загрузки в S3
   */
  async initUpload(
    req: InitUploadRequest,
  ): Promise<NiceResponse<InitUploadResponse>> {
    const response = await this.api.client.post<
      NiceResponse<InitUploadResponse>
    >(`${this.path}/upload/init`, req);
    return response.data;
  }

  /**
   * Завершение загрузки изображения
   * Обрабатывает загруженное изображение и создает варианты
   */
  async finalizeUpload(
    req: FinalizeUploadRequest,
  ): Promise<NiceResponse<FinalizeUploadResponse>> {
    const response = await this.api.client.post<
      NiceResponse<FinalizeUploadResponse>
    >(`${this.path}/upload/finalize`, req);
    return response.data;
  }

  /**
   * Добавление видео по URL
   * Поддерживает YouTube URLs
   */
  async addVideo(
    req: AddVideoRequest,
  ): Promise<NiceResponse<AddVideoResponse>> {
    const response = await this.api.client.post<NiceResponse<AddVideoResponse>>(
      `${this.path}/video`,
      req,
    );
    return response.data;
  }

  /**
   * Получение медиа-актива по ID
   */
  async getById(id: string): Promise<NiceResponse<MediaAssetResponse>> {
    const response = await this.api.client.get<
      NiceResponse<MediaAssetResponse>
    >(`${this.path}/${id}`);
    return response.data;
  }

  /**
   * Получение списка медиа-активов с фильтрацией
   */
  async getList(
    params?: MediaListParams,
  ): Promise<NiceResponse<MediaListResponse>> {
    const queryParams = new URLSearchParams();

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, String(value));
        }
      });
    }

    const queryString = queryParams.toString();
    const url = queryString ? `${this.path}?${queryString}` : this.path;

    const response = await this.api.client.get<NiceResponse<MediaListResponse>>(
      url,
    );
    return response.data;
  }

  /**
   * Удаление медиа-актива
   * Удаляет медиа-актив и все его файлы из S3
   */
  async delete(id: string): Promise<NiceResponse<MediaActionResponse>> {
    const response = await this.api.client.delete<
      NiceResponse<MediaActionResponse>
    >(`${this.path}/${id}`);
    return response.data;
  }

  /**
   * Прикрепление медиа к отзыву
   */
  async attachToReview(
    id: string,
    req: AttachMediaRequest,
  ): Promise<NiceResponse<MediaActionResponse>> {
    const response = await this.api.client.post<
      NiceResponse<MediaActionResponse>
    >(`${this.path}/${id}/attach`, req);
    return response.data;
  }

  /**
   * Открепление медиа от отзыва
   */
  async detachFromReview(
    id: string,
    req: DetachMediaRequest,
  ): Promise<NiceResponse<MediaActionResponse>> {
    const response = await this.api.client.delete<
      NiceResponse<MediaActionResponse>
    >(`${this.path}/${id}/detach`, { data: req });
    return response.data;
  }

  // === Удобные методы-хелперы ===

  /**
   * Полный цикл загрузки изображения
   * @param file - файл для загрузки
   * @param alt - альтернативный текст
   * @returns Promise с результатом загрузки
   */
  async uploadImage(
    file: File,
    alt?: string,
  ): Promise<NiceResponse<FinalizeUploadResponse>> {
    try {
      // 1. Инициализация загрузки
      const initResponse = await this.initUpload({
        filename: file.name,
        file_type: file.type,
        file_size: file.size,
      });

      if (!initResponse.success || !initResponse.body) {
        return {
          success: false,
          body: null,
          error: initResponse.error || 'Ошибка инициализации загрузки',
          code: initResponse.code || 400,
          total: null,
        };
      }

      // 2. Загрузка файла в S3
      const uploadResponse = await fetch(initResponse.body.presigned_url, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      });

      if (!uploadResponse.ok) {
        return {
          success: false,
          body: null,
          error: 'Ошибка загрузки файла в S3',
          code: uploadResponse.status,
          total: null,
        };
      }

      // 3. Финализация загрузки
      const finalizeResponse = await this.finalizeUpload({
        upload_id: initResponse.body.upload_id,
        alt,
      });

      return finalizeResponse;
    } catch (error) {
      return {
        success: false,
        body: null,
        error: error instanceof Error ? error.message : 'Неизвестная ошибка',
        code: 500,
        total: null,
      };
    }
  }

  /**
   * Получение медиа для конкретного отзыва
   */
  async getReviewMedia(
    reviewId: string,
    type?: 'image' | 'video',
    role?: 'gallery' | 'poster' | 'attachment',
  ): Promise<NiceResponse<MediaListResponse>> {
    return this.getList({
      review_id: reviewId,
      type,
      role,
    });
  }

  // Простые методы для MediaSelector
  async getSimpleMediaFiles(params?: {
    page?: number;
    limit?: number;
    type?: 'image' | 'video';
  }): Promise<NiceResponse<MediaListResponse>> {
    return this.getList({
      page: params?.page || 1,
      limit: params?.limit || 20,
      type: params?.type || 'image',
    });
  }

  async uploadSimpleFile(
    file: File,
    altText?: string,
  ): Promise<
    NiceResponse<FinalizeUploadResponse> | NiceResponse<InitUploadResponse>
  > {
    // Сначала инициируем загрузку
    const initResponse = await this.initUpload({
      filename: file.name,
      file_type: file.type,
      file_size: file.size,
    });

    if (!initResponse.success || !initResponse.body) {
      return initResponse;
    }

    const { upload_id, presigned_url } = initResponse.body;

    try {
      // Загружаем файл напрямую в S3
      await fetch(presigned_url, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      });

      // Финализируем загрузку
      const finalizeResponse = await this.finalizeUpload({
        upload_id,
        alt: altText,
      });

      return finalizeResponse;
    } catch (error) {
      return {
        success: false,
        error: 'Upload failed',
        body: null,
        total: null,
        code: null,
      };
    }
  }
}
